import { useMemo } from 'react';
import * as Dfns from 'date-fns/fp';

import { formatMoney, formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { abbreviateNumber } from '@src/utils/data';
import { Channel10a, Channel11a, Channel4, Danger300 } from '@src/utils/colors';

import { ChartContainer } from '@Components/ChartContainer';
import { LineChart } from '@Components/Charts/LineChart';
import { PercentChange } from '@Components/PercentChange';
import { Size, StretchMode } from '@Components/designConstants';

import './CompetitiveChartBox.scss';

export const getPrevWeekTrend = (values: number[]): number | null => {
  const { length } = values;
  if (!length) {
    return null;
  }
  return length === 1 ? 1 : (values[length - 1] - values[length - 2]) / values[length - 2];
};

export const getAllWeeksTrend = (values: number[]): number | null => {
  const { length } = values;
  if (!length) {
    return null;
  }
  if (length === 1) {
    return 1;
  }
  const previousValues = values.slice(0, -1);
  const previousAverage = previousValues.reduce((a, b) => a + b, 0) / previousValues.length;
  const mostRecent = values[values.length - 1];
  return (mostRecent - previousAverage) / previousAverage;
};

const getChartColor = (title: string, isMoney: boolean): string => {
  if (isMoney) {
    return Channel4;
  }
  if (title.toLowerCase().includes('creative')) {
    return Channel11a;
  }
  return Channel10a;
};

const formatWeek = (date: string): string => Dfns.format('MMM d')(Dfns.parseISO(date));

interface TransformedData {
  date: string;
  count: number;
}
interface CompetitiveChartBoxProps {
  transformedData: TransformedData[];
  title: string;
}

export const CompetitiveChartBox = ({ transformedData, title }: CompetitiveChartBoxProps) => {
  const isMoney = title.toLowerCase().includes('spend');
  const { prevWeekTrend, allWeeksTrend, prevWeeksCount, mostRecentWeekValue } = useMemo(() => {
    const values = transformedData.map(row => row.count);
    return {
      prevWeekTrend: getPrevWeekTrend(values),
      allWeeksTrend: getAllWeeksTrend(values),
      prevWeeksCount: values.length - 1,
      mostRecentWeekValue: values.length ? values[values.length - 1] : null
    };
  }, [transformedData]);

  if (mostRecentWeekValue == null) {
    return <ChartContainer title={title} className='competitive-chart-box' />;
  }

  const formatValue = (value: number): string =>
    isMoney ? formatMoney(value) : formatNumber(value);
  const formatTick = (value: number): string => `${isMoney ? '$' : ''}${abbreviateNumber(value)}`;
  const chartColor = getChartColor(title, isMoney);

  return (
    <ChartContainer
      title={title}
      className='competitive-chart-box'
      subHeader={
        <div className='competitive-chart-box-stats'>
          <div className='competitive-chart-box-stat'>
            <span className='stat-label'>Latest week</span>
            <span className='stat-value'>{formatValue(mostRecentWeekValue)}</span>
          </div>
          <TrendStat label='vs. prev week' trend={prevWeekTrend} />
          <TrendStat label={`vs. prev ${prevWeeksCount} wk avg`} trend={allWeeksTrend} />
        </div>
      }
    >
      <div className='competitive-chart-box-chart'>
        <LineChart
          data={transformedData}
          xAxisDataKey='date'
          xAxisTickFormatter={formatWeek}
          lineDataKeys={[{ dataKey: 'count', name: title }]}
          colorMappings={[{ name: title, color: chartColor }]}
          useLeftYAxis
          yAxisTickFormatter={formatTick}
          tooltipFormatter={formatValue}
          customTooltipHeader={date => `Week of ${formatWeek(date)}`}
          showLegend={false}
        />
      </div>
    </ChartContainer>
  );
};

interface TrendStatProps {
  label: string;
  trend: number | null;
}

const TrendStat = ({ label, trend }: TrendStatProps) => (
  <div className='competitive-chart-box-stat'>
    <span className='stat-label'>{label}</span>
    {trend == null ? (
      <span className='stat-value'>-</span>
    ) : (
      <PercentChange
        percentChange={trend * 100}
        negativeColor={Danger300}
        abbreviate={false}
        size={Size.SM}
        stretchMode={StretchMode.FIT}
      />
    )}
  </div>
);
